// src/hooks/useCdrBuckets.js
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

const CDR_TECH_QUERY = gql`
  query CdrTech($scenario: String, $from: Int, $to: Int) {
    timeSeries(
      view: CDR_BY_PROCSET
      scenario: $scenario
      fromYear: $from
      toYear: $to
    ) {
      year
      group
      value
    }
  }
`;

// Stable IDs
const ID_SOILS = "SOILS";
const ID_BECCS = "BECCS";
const ID_DAC   = "DAC";

const LABELS = {
  [ID_SOILS]: "Soils and Trees (Your Scenario)",
  [ID_BECCS]: "Bioenergy with Carbon Capture (Your Scenario)",
  [ID_DAC]:   "Direct Air Capture (Your Scenario)",
};

const COLORS = {
  [ID_SOILS]: { border: "#2ca02c", bg: "rgba(44,160,44,0.18)" },
  [ID_BECCS]: { border: "#ff7f0e", bg: "rgba(255,127,14,0.18)" },
  [ID_DAC]:   { border: "#1f77b4", bg: "rgba(31,119,180,0.18)" },
  TOTAL:      { border: "#111827", bg: "rgba(17,24,39,0.08)" },
};

const clean = (s) => String(s || "").trim().replace(/\s+/g, " ").toUpperCase();
const sig   = (s) => clean(s).replace(/[^A-Z0-9]/g, "");

// Map raw name -> bucket ID
function bucketIdFor(group) {
  const k = sig(group);
  if (k.includes("PRCDAC") || k === "DAC" || k.includes("DIRECTAIRCAPTURE")) return ID_DAC;
  if (k === "ELCFROMBIOCCSALL" || (k.includes("CCS") && (k.includes("BIO") || k.includes("HYG") || k.includes("SYNFUEL")))) return ID_BECCS;
  if (k.startsWith("LUAFFORESTATION") || k.startsWith("LUENERGYFORESTRY") || k.startsWith("LUSOIL")) return ID_SOILS;
  return null;
}

export function useCdrBuckets({
  scenario,
  from = 2020,
  to = 2050,
  step = 5,
  unitScale = 1,
  includeTotalInPrimary = false,   // <- rename to clarify intent
}) {
  const { data, loading, error } = useQuery(CDR_TECH_QUERY, {
    variables: { scenario, from, to },
    fetchPolicy: "cache-first",
    skip: !scenario,
  });

  // rows -> { year, id, abs(value) }
  const rows = useMemo(() => {
    const src = data?.timeSeries ?? [];
    return src
      .map((r) => {
        const yr = Number(r.year);
        const id = bucketIdFor(r.group);
        if (!id || !Number.isFinite(yr)) return null;
        return { year: yr, id, value: Math.abs((Number(r.value) || 0) * unitScale) };
      })
      .filter(Boolean)
      .filter((r) => r.year >= from && r.year <= to);
  }, [data, from, to, unitScale]);

  // labels
  const labels = useMemo(() => {
    const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
    if (!step || step <= 1) return years;
    return years.filter((y) => (y - from) % step === 0);
  }, [rows, from, step]);

  // id -> Map(year -> sum)
  const byId = useMemo(() => {
    const m = new Map();
    rows.forEach(({ year, id, value }) => {
      if (!m.has(id)) m.set(id, new Map());
      m.get(id).set(year, (m.get(id).get(year) || 0) + value);
    });
    return m;
  }, [rows]);

  const ORDER = [ID_SOILS, ID_BECCS, ID_DAC];

  // per-year totals (sum of the three buckets, zeros if missing)
  const totals = useMemo(() => {
    return labels.map((year) =>
      ORDER.reduce((sum, id) => sum + (byId.get(id)?.get(year) || 0), 0)
    );
  }, [labels, byId]);

  // Build bucket datasets
  const bucketDatasets = useMemo(() => {
    return ORDER.map((id) => {
      const { border, bg } = COLORS[id];
      const data = labels.map((y) => byId.get(id)?.get(y) ?? 0);
      return {
        label: LABELS[id],
        id,
        data,
        borderColor: border,
        backgroundColor: bg,
        fill: "origin",
        tension: 0.3,
        pointRadius: 0,
      };
    });
  }, [labels, byId]);

  // A ready-to-plot total dataset (not included by default in primary)
  const totalDataset = useMemo(() => ({
    label: "Total",
    isTotal: true,
    data: totals,
    borderColor: COLORS.TOTAL.border,
    backgroundColor: COLORS.TOTAL.bg,
    fill: false,
    borderWidth: 1.5,
    borderDash: [5, 4],
    tension: 0.3,
    pointRadius: 0,
  }), [totals]);

  // Final datasets for the primary chart
  const datasets = useMemo(() => {
    return includeTotalInPrimary ? [totalDataset, ...bucketDatasets] : bucketDatasets;
  }, [bucketDatasets, includeTotalInPrimary, totalDataset]);

  return { labels, datasets, totalDataset, totals, loading, error };
}
